import FaqPage from './faq';
import React from 'react';
import Loader from 'shared/loader';
import { useWebsite, WebsiteOutlet, WebsitePage } from './website';
import ErrorPage from './components/error-view';
import { ScrollToTop } from './scroll-to-top';
import { Navigate, redirect, useLocation } from 'react-router';

// Booking and payment screens are loaded only when rendered. Marketing pages
// keep their SSR content while avoiding these screens' JavaScript on first load.
function lazyView(load: () => Promise<{ default: React.ComponentType }>) {
  // A preloaded route must render synchronously inside the view transition,
  // including its first visit; React.lazy alone would still show its fallback.
  let LoadedView: React.ComponentType | undefined;
  const preload = async () => {
    const module = await load();
    LoadedView = module.default;
    return module;
  };
  const SuspendedView = React.lazy(preload);
  function LazyView() {
    const View = LoadedView || SuspendedView;
    return (
      <React.Suspense fallback={<Loader />}>
        <View />
      </React.Suspense>
    );
  }
  return Object.assign(LazyView, { preload });
}

const BookView = lazyView(() => import('./components/book'));
const BookingView = lazyView(() => import('./components/booking'));
const FormView = lazyView(() => import('./components/form'));
const LocationsView = lazyView(() => import('./components/locations'));
const NotificationsView = lazyView(() => import('./components/notifications'));
const PayAccountView = lazyView(() => import('./components/pay-account'));
const PayInvoiceView = lazyView(() => import('./components/pay-invoice'));
const RequestPrescriptionView = lazyView(
  () => import('./components/request-prescription'),
);
const NewBookingView = lazyView(() => import('./components/new-booking'));
const ServicesView = lazyView(() => import('./components/services'));
const TestView = lazyView(() => import('./components/test'));
const BookConfirmView = lazyView(
  () => import('./components/book/confirm-view'),
);

const LegacyContactRedirect = () => {
  const { search } = useLocation();

  return <Navigate to={{ pathname: '/about', search }} replace />;
};

const Home = () => (useWebsite()?.website ? <WebsitePage /> : <ServicesView />);
const About = () =>
  useWebsite()?.website ? <WebsitePage /> : <LocationsView />;
const Contact = () =>
  useWebsite()?.website ? <WebsitePage /> : <LegacyContactRedirect />;

// Resolve the initial public screen before hydration so lazy code loading does
// not replace already visible server content with a spinner.
export async function preloadPublicPage(pathname: string, hasWebsite: boolean) {
  if (pathname === '/book' || (!hasWebsite && pathname === '/'))
    await ServicesView.preload();
  else if (!hasWebsite && pathname === '/about') await LocationsView.preload();
}

export const createPublicRoutes = ({
  loadBookingPage,
  loadAboutPage,
  hasWebsite = false,
}: {
  loadBookingPage?: () => Promise<null>;
  loadAboutPage?: () => Promise<null>;
  hasWebsite?: boolean;
} = {}) => [
  {
    element: <ScrollToTop />,
    children: [
      {
        element: <WebsiteOutlet />,
        errorElement: <ErrorPage />,
        children: [
          {
            id: 'public-home',
            path: '/',
            element: <Home />,
            loader:
              !hasWebsite && loadBookingPage
                ? async () => {
                    await Promise.all([
                      ServicesView.preload(),
                      loadBookingPage(),
                    ]);
                    return null;
                  }
                : undefined,
          },
          {
            id: 'booking-catalogue',
            path: '/book',
            element: <ServicesView />,
            loader: loadBookingPage
              ? async () => {
                  await Promise.all([
                    ServicesView.preload(),
                    loadBookingPage(),
                  ]);
                  return null;
                }
              : undefined,
          },
          { path: '/faq', element: <FaqPage /> },
          { path: '*', element: <WebsitePage /> },
          {
            path: '/bookings/new',
            element: <NewBookingView />,
          },
          {
            path: '/bookings/new/details',
            element: <BookView />,
          },
          {
            path: '/bookings/new/confirm',
            element: <BookConfirmView />,
          },
          {
            path: '/services/:serviceId',
            loader: ({ request, params }) => {
              const search = new URL(request.url).searchParams;
              search.set('service_id', params.serviceId);
              return redirect(`/book?${search}`);
            },
          },
          {
            path: '/bookings/:bookingId',
            element: <BookingView />,
          },
          {
            id: 'public-about',
            path: '/about',
            element: <About />,
            loader:
              !hasWebsite && loadAboutPage
                ? async () => {
                    await Promise.all([
                      LocationsView.preload(),
                      loadAboutPage(),
                    ]);
                    return null;
                  }
                : undefined,
          },
          {
            path: '/contact',
            element: <Contact />,
          },
          {
            path: '/request-prescription',
            element: <RequestPrescriptionView />,
          },
          {
            path: '/forms/:formId',
            element: <FormView />,
          },
          {
            path: '/pay-account/:clientId',
            element: <PayAccountView />,
          },
          {
            path: '/pay-invoice/:invoiceId',
            element: <PayInvoiceView />,
          },
          {
            path: '/test',
            element: <TestView />,
          },
          {
            path: `/notifications/:clientId`,
            element: <NotificationsView />,
          },
        ],
      },
    ],
  },
];

export default createPublicRoutes();
